import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { notifyFundEmail } from '@/lib/notify';
import { useToast } from '@/components/base/Toast';

const purities = ['99.99%', '99.95%', '99.9%'];

function nowText() {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

export default function MaterialDepositPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [weight, setWeight] = useState('');
  const [purity, setPurity] = useState('');
  const [screenshots, setScreenshots] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const inputWeight = parseFloat(weight) || 0;

  const canSubmit = () => {
    if (inputWeight <= 0) return false;
    if (!purity) return false;
    if (screenshots.length === 0) return false;
    return true;
  };

  const handleWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (/^\d*\.?\d{0,3}$/.test(val)) {
      setWeight(val);
    }
  };

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('请先登录');

      const newUrls: string[] = [];
      for (let i = 0; i < Math.min(files.length, 5 - screenshots.length); i++) {
        const file = files[i];
        if (!file.type.startsWith('image/')) continue;
        if (file.size > 5 * 1024 * 1024) {
          showToast('单张图片不能超过 5MB', 'error');
          continue;
        }

        const timestamp = Date.now();
        const ext = file.name.split('.').pop() || 'jpg';
        const path = `deposits/material/${user.id}/${timestamp}_${i}.${ext}`;

        const { error: uploadError } = await supabase.storage.from('public').upload(path, file, {
          upsert: true,
          contentType: file.type,
        });
        if (uploadError) {
          console.error('上传失败:', uploadError);
          continue;
        }

        const { data: publicUrlData } = supabase.storage.from('public').getPublicUrl(path);
        newUrls.push(publicUrlData.publicUrl);
      }

      setScreenshots((prev) => [...prev, ...newUrls].slice(0, 5));
    } catch (err) {
      showToast(err instanceof Error ? err.message : '上传失败', 'error');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [screenshots.length, showToast]);

  const handleRemoveScreenshot = (index: number) => {
    setScreenshots((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (submitting) return;
    if (!canSubmit()) return;
    setSubmitting(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('登录已失效');

      const remark = JSON.stringify({
        weight: inputWeight,
        purity,
        source: 'company',
        screenshots: screenshots,
      });
      const createTime = nowText();

      const { error } = await supabase.from('deposits').insert({
        user_id: user.id,
        amount: 0,
        channel: '金安代提',
        channel_id: null,
        account: null,
        payer_name: null,
        bank_name: null,
        remark,
        status: 'pending',
        create_time: createTime,
      });

      if (error) throw error;

      notifyFundEmail({
        userId: user.id,
        type: 'deposit',
        title: '金安代提（入金）',
        amount: `${inputWeight} 克`,
        items: [
          { label: '黄金成色', value: purity },
          { label: '黄金来源', value: '金安代提' },
        ],
        time: createTime,
      });

      setSubmitted(true);
    } catch (e) {
      showToast(e instanceof Error ? e.message : '提交失败', 'error');
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-brown-50 flex flex-col items-center justify-center px-6">
        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4">
          <i className="ri-check-line text-green-500 text-4xl"></i>
        </div>
        <h2 className="text-brown-900 text-lg font-bold mb-2">申请已提交</h2>
        <p className="text-brown-500 text-sm text-center">
          金安代提 {inputWeight}克 {purity}
        </p>
        <p className="text-brown-400 text-xs mt-2">十分钟之内审核，请注意接听您合作公司电话</p>
        <button
          onClick={() => {
            setSubmitted(false);
            setWeight('');
            setPurity('');
            setScreenshots([]);
          }}
          className="mt-6 px-6 py-2.5 rounded-full bg-gold-500 text-brown-950 text-sm font-medium cursor-pointer whitespace-nowrap"
        >
          继续申请
        </button>
        <button
          onClick={() => navigate('/profile/transactions')}
          className="mt-3 text-gold-500 text-sm cursor-pointer"
        >
          查看记录
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brown-50 pb-8">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-brown-950 px-4 py-3 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center cursor-pointer">
          <i className="ri-arrow-left-line text-gold-400 text-lg"></i>
        </button>
        <span className="text-gold-400 text-sm font-bold">金安代提</span>
        <div className="w-8" />
      </div>

      <div className="px-3 mt-3 space-y-3">
        {/* 黄金重量 */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <label className="block text-brown-700 text-sm font-medium mb-3">黄金重量</label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              inputMode="decimal"
              value={weight}
              onChange={handleWeightChange}
              placeholder="请输入黄金重量"
              className="flex-1 text-sm text-brown-900 placeholder:text-brown-300 border border-brown-200 rounded-xl px-4 py-3 bg-white outline-none focus:border-gold-500 transition-colors"
            />
            <span className="text-brown-500 text-sm px-3 py-3">克</span>
          </div>
        </div>

        {/* 黄金成色 */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <label className="block text-brown-700 text-sm font-medium mb-3">黄金成色</label>
          <div className="relative">
            <select
              value={purity}
              onChange={(e) => setPurity(e.target.value)}
              className="w-full text-sm text-brown-900 placeholder:text-brown-300 border border-brown-200 rounded-xl px-4 py-3 bg-white outline-none focus:border-gold-500 transition-colors appearance-none cursor-pointer"
            >
              <option value="">请选择成色</option>
              {purities.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <span className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-brown-400 pointer-events-none">
              <i className="ri-arrow-down-s-line text-sm"></i>
            </span>
          </div>
        </div>

        {/* 黄金来源 */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <label className="block text-brown-700 text-sm font-medium mb-3">黄金来源</label>
          <button className="w-full py-3 rounded-xl text-sm font-medium bg-gold-500 text-brown-950 border border-gold-500 cursor-default">
            金安代提
          </button>
        </div>

        {/* 截图上传 */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <p className="text-brown-700 text-sm font-medium mb-1">
            请上传您与板料公司的聊天记录，记录包括地址以及提货码
          </p>
          <p className="text-brown-400 text-xs mb-3">
            上传截图（必填）
          </p>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileChange}
            className="hidden"
          />

          {/* 已上传图片预览 */}
          {screenshots.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {screenshots.map((url, idx) => (
                <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden border border-brown-200">
                  <img src={url} alt={`截图${idx + 1}`} className="w-full h-full object-cover" />
                  <button
                    onClick={() => handleRemoveScreenshot(idx)}
                    className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/50 text-white flex items-center justify-center cursor-pointer"
                  >
                    <span className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-close-line text-xs"></i>
                    </span>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* 上传按钮 */}
          {screenshots.length < 5 && (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full py-4 rounded-xl border-2 border-dashed border-brown-200 flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-gold-400 transition-colors"
            >
              {uploading ? (
                <span className="w-5 h-5 border-2 border-gold-500/30 border-t-gold-500 rounded-full animate-spin"></span>
              ) : (
                <>
                  <span className="w-6 h-6 flex items-center justify-center text-brown-400">
                    <i className="ri-add-line text-lg"></i>
                  </span>
                  <span className="text-brown-500 text-sm">点击上传图片/文件</span>
                  <span className="text-brown-400 text-xs">支持图片格式，最多5张</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* 底部提示 */}
      <div className="mx-3 mt-5 flex items-center justify-center gap-1.5">
        <span className="w-4 h-4 flex items-center justify-center text-brown-400">
          <i className="ri-information-line text-sm"></i>
        </span>
        <p className="text-brown-500 text-xs">十分钟之内审核，请注意接听您合作公司电话</p>
      </div>

      {/* 提交按钮 */}
      <div className="px-3 mt-4 mb-8">
        <button
          onClick={handleSubmit}
          disabled={!canSubmit() || submitting}
          className={`w-full py-3.5 rounded-xl text-white text-sm font-semibold cursor-pointer transition-all ${
            canSubmit() && !submitting
              ? 'bg-gradient-to-r from-amber-600 to-amber-500 active:scale-[0.98] shadow-sm shadow-amber-500/20'
              : 'bg-gray-300 cursor-not-allowed'
          }`}
        >
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
              提交中...
            </span>
          ) : (
            '提交申请'
          )}
        </button>
      </div>
    </div>
  );
}